import type { TFunction } from 'i18next'

export interface StorefrontSchedule {
  acceptingOrders: boolean
  nextOpenTime: string | null
  timezone: string
  serverTime?: string | null
}

export type OpeningRelativeDay = 'today' | 'tomorrow' | 'later'

export interface OpeningLabel {
  relativeDay: OpeningRelativeDay
  time: string
  date: string | null
}

interface LocalDateParts {
  year: number
  month: number
  day: number
}

function localDateParts(date: Date, timezone: string): LocalDateParts {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const map = new Map(fmt.formatToParts(date).map(p => [p.type, p.value]))
  return {
    year: Number(map.get('year')),
    month: Number(map.get('month')),
    day: Number(map.get('day')),
  }
}

function sameDate(a: LocalDateParts, b: LocalDateParts): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day
}

function nextCalendarDate(date: LocalDateParts): LocalDateParts {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + 1))
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  }
}

function formatTime(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date)
}

function formatDate(date: Date, timezone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: 'numeric',
    month: 'long',
  }).format(date)
}

/**
 * Describe the next opening instant relative to "now" in the location timezone.
 * Returns null when the instant is missing or invalid.
 */
export function formatOpeningLabel(
  nextOpenTime: string,
  timezone: string,
  now: Date = new Date(),
  locale = 'ru',
): OpeningLabel | null {
  const target = new Date(nextOpenTime)
  if (Number.isNaN(target.getTime())) return null

  try {
    const nowDate = localDateParts(now, timezone)
    const targetDate = localDateParts(target, timezone)

    const relativeDay: OpeningRelativeDay = sameDate(nowDate, targetDate)
      ? 'today'
      : sameDate(nextCalendarDate(nowDate), targetDate)
        ? 'tomorrow'
        : 'later'

    return {
      relativeDay,
      time: formatTime(target, timezone),
      date: relativeDay === 'later' ? formatDate(target, timezone, locale) : null,
    }
  } catch {
    return null
  }
}

/**
 * Build the customer-facing closed notice. The time is rendered in the
 * location timezone and never invented when it is unknown.
 */
export function buildClosedNoticeMessage(
  t: TFunction,
  name: string,
  schedule: Pick<StorefrontSchedule, 'nextOpenTime' | 'timezone'>,
  now: Date = new Date(),
  locale = 'ru',
): string {
  if (!schedule.nextOpenTime) {
    return t('storefront.closedNoticeNoTime', { name })
  }

  const label = formatOpeningLabel(schedule.nextOpenTime, schedule.timezone, now, locale)
  if (!label) {
    return t('storefront.closedNoticeNoTime', { name })
  }

  if (label.relativeDay === 'tomorrow') {
    return t('storefront.closedNoticeTomorrow', { name, time: label.time })
  }
  if (label.relativeDay === 'today') {
    return t('storefront.closedNoticeToday', { name, time: label.time })
  }
  return t('storefront.closedNoticeLater', { name, date: label.date, time: label.time })
}
